import { Forum, Contact } from '@/types';
import { zohoAuthService } from './zohoAuth';

class ZohoApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_ZOHO_API_BASE_URL || 'https://forums.zoho.com/api/v1';
  }

  // Méthode privée pour faire des requêtes authentifiées
  private async makeAuthenticatedRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await zohoAuthService.getValidToken();
    
    if (!token) {
      throw new Error('Token d\'authentification non disponible');
    }

    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur API Zoho: ${response.status} ${response.statusText}`);
    }

    return response;
  }

  // Récupérer la liste des forums
  async getForums(): Promise<Forum[]> {
    try {
      const response = await this.makeAuthenticatedRequest('/forums');
      const data = await response.json();
      
      if (data.success) {
        return data.data.map((forum: any) => ({
          id: forum.id,
          name: forum.name,
          description: forum.description,
          url: forum.url,
          category: forum.category || 'general',
          isActive: forum.is_active,
          defaultContacts: []
        }));
      } else {
        throw new Error(data.message || 'Erreur lors de la récupération des forums');
      }
    } catch (error) {
      console.error('Erreur getForums:', error);
      throw error;
    }
  }

  // Récupérer les utilisateurs d'un forum
  async getForumUsers(forumId: string): Promise<Contact[]> {
    try {
      const response = await this.makeAuthenticatedRequest(`/forums/${forumId}/users`);
      const data = await response.json();
      
      if (data.success) {
        return data.data.map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          isDefault: false
        }));
      } else {
        throw new Error(data.message || 'Erreur lors de la récupération des utilisateurs');
      }
    } catch (error) {
      console.error('Erreur getForumUsers:', error);
      throw error;
    }
  }

  // Créer un nouveau topic dans un forum
  async createTopic(forumId: string, topicData: {
    title: string;
    content: string;
    category: string;
    tags?: string[];
    notifyUsers?: string[];
  }): Promise<{ id: string; url: string }> {
    try {
      const response = await this.makeAuthenticatedRequest(`/forums/${forumId}/topics`, {
        method: 'POST',
        body: JSON.stringify({
          title: topicData.title,
          content: topicData.content,
          category: topicData.category,
          tags: topicData.tags || [],
          notify_users: topicData.notifyUsers || []
        })
      });

      const data = await response.json();
      
      if (data.success) {
        return {
          id: data.data.id,
          url: data.data.url
        };
      } else {
        throw new Error(data.message || 'Erreur lors de la création du topic');
      }
    } catch (error) {
      console.error('Erreur createTopic:', error);
      throw error;
    }
  }

  // Uploader un fichier
  async uploadAttachment(file: File): Promise<{ id: string; url: string }> {
    try {
      const token = await zohoAuthService.getValidToken();
      
      if (!token) {
        throw new Error('Token d\'authentification non disponible');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${this.baseUrl}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Erreur upload: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          id: data.data.id,
          url: data.data.url
        };
      } else {
        throw new Error(data.message || 'Erreur lors de l\'upload du fichier');
      }
    } catch (error) {
      console.error('Erreur uploadAttachment:', error);
      throw error;
    }
  }

  // Envoyer un message à plusieurs forums
  async sendMessageToForums(messageData: {
    title: string;
    content: string;
    category: string;
    tags: string[];
    forums: string[];
    attachments: File[];
    notifyUsers: string[];
  }): Promise<{ success: boolean; results: Array<{ forumId: string; topicId?: string; error?: string }> }> {
    const results: Array<{ forumId: string; topicId?: string; error?: string }> = [];

    // Upload des pièces jointes d'abord
    const attachmentIds: string[] = [];
    if (messageData.attachments.length > 0) {
      for (const file of messageData.attachments) {
        try {
          const attachment = await this.uploadAttachment(file);
          attachmentIds.push(attachment.id);
        } catch (error) {
          console.error(`Erreur upload fichier ${file.name}:`, error);
          // Continuer sans ce fichier
        }
      }
    }

    // Envoyer le message à chaque forum
    for (const forumId of messageData.forums) {
      try {
        const topic = await this.createTopic(forumId, {
          title: messageData.title,
          content: messageData.content,
          category: messageData.category,
          tags: messageData.tags,
          notifyUsers: messageData.notifyUsers
        });

        results.push({
          forumId,
          topicId: topic.id
        });

        // Attendre un peu entre chaque envoi pour respecter les rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        results.push({
          forumId,
          error: error instanceof Error ? error.message : 'Erreur inconnue'
        });
      }
    }

    const success = results.some(r => r.topicId);
    return { success, results };
  }

  // Tester la connexion
  async testConnection(): Promise<boolean> {
    try {
      await this.makeAuthenticatedRequest('/forums');
      return true;
    } catch (error) {
      console.error('Erreur testConnection:', error);
      return false;
    }
  }
}

export const zohoApiService = new ZohoApiService(); 